import type { Asset } from '../../../../api/assetAdministrationBackendCodec.ts';
import { useState } from 'react';
import { useAppDispatch } from '../../../../../configuration/setup/hooks.ts';
import { useIntl } from 'react-intl';
import { useDeleteAssetMutation } from '../../../../../services/assetsApi.ts';
import { sleep } from '../../../../../utils.ts';
import { assetModified } from '../assetSidebarSlice.ts';
import Notification from '@rio-cloud/rio-uikit/Notification';
import { AssetDeletionDialog } from '../../dialogs/AssetDeletionDialog.tsx';
import type { ContractDialogState } from '../../dialogs/contractDialogUtils.ts';
import { getAccountsWithContracts } from '../../../../api/contracts/getAccountsWithContracts.ts';
import { ContractDialog } from '../../dialogs/ContractDialog.tsx';

export const DeleteAsset = ({ asset }: { asset: Asset }) => {
    const [showDialog, setShowDialog] = useState(false);
    const dispatch = useAppDispatch();
    const intl = useIntl();
    const [deleteAsset] = useDeleteAssetMutation();
    const miniAsset = [
        {
            id: asset.id,
            vin: asset.identification,
            accountId: asset.account_id,
            status: asset.status,
        },
    ];

    const [contractDialogState, setContractDialogState] = useState<ContractDialogState>({
        showDialog: false,
        requestState: undefined,
        accountsWithContracts: [],
        errorMessage: undefined,
        checkBoxChecked: false,
        intent: 'delete',
    });
    const updateContractDialogState = (newState: Partial<ContractDialogState>) => {
        setContractDialogState(prevState => ({ ...prevState, ...newState }));
    };

    const initiateDeleteAssets = async () => {
        updateContractDialogState({ requestState: 'loading', showDialog: true });
        try {
            const accountsWithContracts = await getAccountsWithContracts(dispatch, miniAsset);
            if (accountsWithContracts.length > 0) {
                updateContractDialogState({
                    requestState: 'resolved',
                    accountsWithContracts: accountsWithContracts,
                });
            } else {
                updateContractDialogState({ showDialog: false });
                setShowDialog(true);
            }
        } catch (error) {
            console.error(`Could not initiate delete assets: ${error}`);
            updateContractDialogState({ requestState: 'error' });
        }
    };

    const handleDelete = (assetIds: string[], reason: string) => {
        deleteAsset({ assetId: assetIds[0], reason })
            .unwrap()
            .then(() => sleep(500))
            .then(() => dispatch(assetModified()))
            .catch(error => {
                console.error(`Could not delete asset: ${error}`);
                Notification.error(intl.formatMessage({ id: 'assets-helpgang.notification.couldNotDeleteAsset.one' }));
            });
        setShowDialog(false);
    };

    return (
        <>
            <button
                type={'button'}
                className={'btn btn-danger'}
                onClick={initiateDeleteAssets}
                data-cy='delete-asset-button'
            >
                <span className={'uikit-icon icon-trash'} />
                Delete
            </button>
            <ContractDialog
                state={contractDialogState}
                updateState={updateContractDialogState}
                handleConfirm={() => {
                    setShowDialog(true);
                }}
                handleRefresh={initiateDeleteAssets}
            />
            <AssetDeletionDialog
                showDialog={showDialog}
                setShowDeletionDialog={setShowDialog}
                assets={miniAsset}
                handleDeleteConfirm={handleDelete}
            />
        </>
    );
};
